import { fechaYMD } from "./utils";

// Campos "core" del participante que no están en registryConfiguration.formulario
// (esos son datos de estructura del evento, no campos adicionales del
// formulario) pero que de todas formas el organizador quiere poder filtrar
// y agrupar. Cada entrada dice CÓMO sacar el valor de un participante.
export const CAMPOS_CORE = [
  { iDCampo: "rama", etiqueta: "Género / Rama", tipoCampo: "coreSelect" },
  { iDCampo: "modalidad", etiqueta: "Modalidad", tipoCampo: "coreSelect" },
  { iDCampo: "categoria", etiqueta: "Categoría", tipoCampo: "coreSelect" },
  {
    iDCampo: "statusPago.formaPago",
    etiqueta: "Forma de pago",
    tipoCampo: "coreSelect",
  },
  {
    iDCampo: "statusPago.formaInscripcion",
    etiqueta: "Forma de inscripción",
    tipoCampo: "coreSelect",
  },
  {
    iDCampo: "usuarioRegistra",
    etiqueta: "Usuario que registró",
    tipoCampo: "coreSelect",
  },
];

const SIN_DATO = "(sin dato)";
// Prefijo de iDCampo sintético para "¿compró este producto adicional?" —
// ver services/camposAdicionalesUtils.js getCamposProductos(), que arma uno
// de estos campos por cada producto configurado en el evento.
export const PRODUCTO_CAMPO_PREFIJO = "producto_";

// Mismo nombre que usa el resto del Dashboard para los inscritos sin un
// usuario de punto de venta/importación detrás (ver DashboardContainer.jsx
// jsonDashboard.byUser).
export const USUARIO_EN_LINEA = "en línea";

// Resuelve el valor de CUALQUIER campo (core, dinámico/iDCampo, o "compró
// producto X") para un participante — un único punto de resolución para
// que el motor de pivot no tenga que saber la diferencia entre "rama"
// (campo de estructura), "talla" (campo dinámico del formulario, guardado
// como key plana en el participante) y "producto_7" (sintético, resuelto
// desde participant.order).
//
// registryAutorizedUsers (opcional) solo hace falta para "usuarioRegistra":
// un participante SIEMPRE tiene algo guardado en ese campo (a veces el uid
// del propio atleta autoinscribiéndose), pero eso NO significa que haya un
// usuario del staff detrás — mismo criterio que ya usa
// DashboardContainer.jsx (jsonDashboard.byUser): solo cuenta como
// "registrado por X" si la forma de inscripción fue "punto de Venta" o
// "Importación masiva" Y ese uid sigue teniendo acceso autorizado al
// evento; cualquier otro caso se agrupa como "en línea".
export function resolveFieldValue(participant, fieldId, registryAutorizedUsers) {
  if (!participant || !fieldId) return SIN_DATO;

  let valor;
  if (fieldId === "statusPago.formaPago") {
    valor = participant.statusPago && participant.statusPago.formaPago;
  } else if (fieldId === "statusPago.formaInscripcion") {
    valor = participant.statusPago && participant.statusPago.formaInscripcion;
  } else if (fieldId === "usuarioRegistra") {
    const formaInscripcion = participant.statusPago && participant.statusPago.formaInscripcion;
    const esRegistroManual =
      formaInscripcion === "punto de Venta" || formaInscripcion === "Importación masiva";
    const uid = participant.usuarioRegistra;
    const tieneAccesoAlEvento =
      uid && registryAutorizedUsers && registryAutorizedUsers[uid] !== undefined;
    valor = esRegistroManual && tieneAccesoAlEvento ? uid : USUARIO_EN_LINEA;
  } else if (fieldId.indexOf(PRODUCTO_CAMPO_PREFIJO) === 0) {
    const codigoProducto = fieldId.slice(PRODUCTO_CAMPO_PREFIJO.length);
    const tieneProducto = Object.values(participant.order || {}).some(
      (item) => item && item.code === "producto-" + codigoProducto
    );
    valor = tieneProducto ? "Sí" : "No";
  } else {
    valor = participant[fieldId];
  }

  if (valor === undefined || valor === null || valor === "") {
    return SIN_DATO;
  }
  if (typeof valor === "boolean") {
    return valor ? "Sí" : "No";
  }
  return String(valor);
}

// Suma de lo cobrado a un participante (igual que "precioCobrado" en
// DashboardContainer.jsx): todas las líneas de su orden EXCEPTO la de
// "comision" (la comisión en línea de Dashport no cuenta como ingreso del
// organizador).
export function resolverMontoCobrado(participant) {
  if (!participant || !participant.order) return 0;
  let total = 0;
  Object.values(participant.order).forEach((item) => {
    if (!item || item.code === "comision") return;
    total += parseFloat(item.unit_price * item.quantity) || 0;
  });
  return total;
}

// Misma precedencia serverTime -> fechaRegistro que ya usa
// DashboardContainer.jsx para fechaInscritoTimeStamp.
function fechaRegistroTimestamp(participant) {
  return participant.serverTime !== undefined
    ? participant.serverTime
    : new Date(participant.fechaRegistro);
}

// Rango real de fechas de inscripción presentes en los participantes —
// usado para saber entre qué días se puede mover el slider de rango
// (DashboardDateRangePicker.jsx) en el filtro opcional del pivot.
export function rangoFechasRegistro(participants) {
  const lista = Array.isArray(participants)
    ? participants
    : Object.values(participants || {});
  let min = null;
  let max = null;
  lista.forEach((p) => {
    if (!p || p.borrado) return;
    if (!p.statusPago || p.statusPago.status !== "ok") return;
    const key = fechaYMD(fechaRegistroTimestamp(p));
    if (!min || key < min) min = key;
    if (!max || key > max) max = key;
  });
  return { min, max };
}

// config = {
//   filters: [{field, value}],
//   rowField: "rama",
//   colField: "talla" | null,
//   dateFrom: "YYYY-MM-DD" | null,
//   dateTo: "YYYY-MM-DD" | null,
//   aggregation: "count" | "sum",   // "count" = cantidad de inscritos,
//                                   // "sum" = suma de lo cobrado (resolverMontoCobrado)
//   registryAutorizedUsers: {...},  // solo lo usa el campo "usuarioRegistra"
// }
export function computePivot(participants, config) {
  const {
    filters = [],
    rowField,
    colField,
    dateFrom,
    dateTo,
    aggregation = "count",
    registryAutorizedUsers,
  } = config || {};

  const rows = [];
  const cols = [];
  const matrix = {};
  const rowTotals = {};
  const colTotals = {};
  let grandTotal = 0;

  if (!rowField || !participants) {
    return { rows, cols, matrix, rowTotals, colTotals, grandTotal };
  }

  // eventParticipants en Redux es un objeto keyed por id (no un array — ver
  // registry-reducer.js), Object.values() lo normaliza para poder iterarlo
  // igual sin importar si quien llama pasa el objeto crudo o ya un array.
  const listaParticipantes = Array.isArray(participants)
    ? participants
    : Object.values(participants || {});

  listaParticipantes.forEach((participant) => {
    if (!participant || participant.borrado) return;
    // Mismo criterio que ya usa el resto del Dashboard (ver
    // DashboardContainer.jsx) para "inscripción completa": statusPago.status
    // === "ok". Una inscripción incompleta (ej. abandonó el pago en línea a
    // medias) no debe contarse en ningún reporte — no es un inscrito real
    // todavía.
    if (!participant.statusPago || participant.statusPago.status !== "ok") return;

    if (dateFrom || dateTo) {
      const key = fechaYMD(fechaRegistroTimestamp(participant));
      if (dateFrom && key < dateFrom) return;
      if (dateTo && key > dateTo) return;
    }

    for (const f of filters) {
      if (!f || !f.field) continue;
      if (resolveFieldValue(participant, f.field, registryAutorizedUsers) !== f.value) return;
    }

    const rowKey = resolveFieldValue(participant, rowField, registryAutorizedUsers);
    const colKey = colField
      ? resolveFieldValue(participant, colField, registryAutorizedUsers)
      : "_total";
    const valorSumado = aggregation === "sum" ? resolverMontoCobrado(participant) : 1;

    if (!matrix[rowKey]) {
      matrix[rowKey] = {};
      rows.push(rowKey);
      rowTotals[rowKey] = 0;
    }
    if (colField && cols.indexOf(colKey) === -1) {
      cols.push(colKey);
    }
    matrix[rowKey][colKey] = (matrix[rowKey][colKey] || 0) + valorSumado;
    rowTotals[rowKey] += valorSumado;
    colTotals[colKey] = (colTotals[colKey] || 0) + valorSumado;
    grandTotal += valorSumado;
  });

  return { rows, cols, matrix, rowTotals, colTotals, grandTotal };
}
